import React from "react";
import { Box } from "@mui/material";
import { headerOffset } from "components/basic/header";
import Typography from "components/basic/typography";

export interface WizardPageImage {
  src: string;
  alt: string;
  side: "left" | "right";
}

interface WizardPageLayoutProps {
  title: string;
  /** Rendered between the title and the scrolling body. */
  subtitle?: React.ReactNode;
  /** Decorative half-pane, dropped below md so the form gets the whole width. */
  image?: WizardPageImage;
  /** Desktop cap for the form column in px; full width on a phone. */
  contentMaxWidth: number;
  children: React.ReactNode;
  actions: React.ReactNode;
}

const WizardPageLayout: React.FC<WizardPageLayoutProps> = ({
  title,
  subtitle,
  image,
  contentMaxWidth,
  children,
  actions,
}) => {
  const imagePane = image ? (
    <Box
      sx={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
        display: { xs: "none", md: "block" },
      }}
    >
      {/* Absolute so the pane stretches to the form's height instead of the
          image's aspect ratio dragging the whole page past the viewport. */}
      <img
        src={image.src}
        alt={image.alt}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        display: "flex",
        // minHeight, not height: below md the form outgrows the viewport and
        // the page itself has to be what scrolls.
        minHeight: `calc(100dvh - ${headerOffset(1)})`,
      }}
    >
      {image?.side === "left" && imagePane}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: { xs: "flex-start", md: "center" },
          textAlign: "center",
          gap: 2,
          paddingX: { xs: 2, sm: 3 },
          paddingY: { xs: 3, md: 0 },
        }}
      >
        <Typography
          variant="h2"
          align="inherit"
          style={{ fontWeight: "bold" }}
          sx={{
            fontSize: { xs: "1.75rem", sm: "2.125rem", md: "2.5rem" },
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>

        {subtitle}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            maxWidth: { xs: "100%", md: contentMaxWidth },
            // Only capped where the column is centred in a full-height split.
            // Below md it must refuse to shrink into the leftover space, so it
            // grows past the viewport and the page scroll takes over — a phone
            // should never get a nested scroll region here.
            flexShrink: { xs: 0, md: 1 },
            maxHeight: { md: "70vh" },
            overflowY: { xs: "visible", md: "auto" },
          }}
        >
          {children}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2,
            width: "100%",
            maxWidth: { xs: "100%", md: contentMaxWidth },
          }}
        >
          {actions}
        </Box>
      </Box>

      {image?.side === "right" && imagePane}
    </Box>
  );
};

export default WizardPageLayout;
